import React, { useEffect, useState } from 'react';
import AlertHeader from '@components/alerts/alert-header/alert-header';
import { useContextSelector } from 'use-context-selector';
import { useTranslation } from 'next-i18next';
import { useFocusRestoreToChosenElement } from '@hooks/a11y/use-focus-restore';
import AlertManageSnippet from '@components/alerts/alert-manage-snippet/alert-manage-snippet';
import PageAuthenticationGuard from '@components/atoms/authentication/page-authentication-guard/page-authentication-guard';
import clsx from 'clsx';
import { AlertsContext } from '@context/alerts.context';
import GenericPagination from '@components/common/generic-pagination/generic-pagination';
import useUser from '@hooks/user/use-user';
import AlertsManagementCardSkeleton from './alerts-management-card-skeleton';
import styles from './alerts-management.module.scss';

interface AlertManagementProps {
  isLanding: boolean;
}

const AlertManagement: React.FC<AlertManagementProps> = ({ isLanding }) => {
  const { t } = useTranslation();
  const getNewInsAlertsResponse = useContextSelector(AlertsContext, (a) => a.getNewInsAlertsResponse);
  const { isAuthenticated } = useUser();
  const userNewInsAlertsResponse = useContextSelector(AlertsContext, (a) => a.userNewInsAlertsResponse);
  const setUserNewinsAlertsResponse = useContextSelector(AlertsContext, (a) => a.setUserNewinsAlertsResponse);
  const alertsQuery = useContextSelector(AlertsContext, (a) => a.alertsQuery);
  const setAlertsQuery = useContextSelector(AlertsContext, (a) => a.setAlertsQuery);
  const defaultAlertLimit = useContextSelector(AlertsContext, (a) => a.DEFAULT_LIMIT);
  const [itemsPerPage, setItemsPerPage] = useState(defaultAlertLimit);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const triggerFocusOnNextUpdate = useFocusRestoreToChosenElement(
    false,
    userNewInsAlertsResponse?.alerts,
    'alertSnippet_id_',
  );

  useEffect(() => {
    (async () => {
      try {
        if (!isAuthenticated) {
          setUserNewinsAlertsResponse({ alerts: [], metaData: null });
          setErrorMessage('');
          return;
        }
        setLoading(true);
        await getNewInsAlertsResponse(alertsQuery);

        setLoading(false);
      } catch (error) {
        setLoading(false);
        if (error?.response?.data?.errors) {
          setErrorMessage(error);
        }
      }
    })();
  }, [isAuthenticated, alertsQuery]);

  const handleItemsPerPageClick = (count: number) => {
    setItemsPerPage(count);
    setAlertsQuery({
      limit: count,
      offset: 0,
    });
    triggerFocusOnNextUpdate(count === defaultAlertLimit ? 0 : itemsPerPage);
  };

  const handlePaginationChange = (page: number, itemsPerPage: number) => {
    const { limit } = alertsQuery;

    setAlertsQuery({
      limit,
      offset: (page - 1) * itemsPerPage,
    });
  };

  const handleErrorMessage = (error: string) => {
    setErrorMessage(error);
  };

  return (
    <PageAuthenticationGuard
      modalType="login"
      redirectionUrl={t('LINKS.NEW_IN')}
    >
      <main className={styles.manageAlert}>
        <AlertHeader
          isLanding={isLanding}
          alertsList={userNewInsAlertsResponse?.alerts}
          errorMessage={errorMessage}
          loading={loading}
        />
        <div
          className={clsx(styles.manageAlert__wrapper, 'innerContainer')}
          data-cy="alert-management-innerContainer-wrapper"
        >
          {errorMessage && (
            <p
              className={styles.manageAlert__error}
              aria-live="assertive"
            >
              {errorMessage}
            </p>
          )}
          {loading && (
            <ul className={styles.manageAlert__wrapper__loader}>
              {Array.from(Array(16).keys()).map((key) => (
                <li key={key}>
                  <AlertsManagementCardSkeleton />
                </li>
              ))}
            </ul>
          )}
          {userNewInsAlertsResponse?.alerts?.length > 0 && (
            <ul
              className={clsx(styles.manageAlert__list, '')}
              data-cy="alert-management-list"
            >
              {userNewInsAlertsResponse?.alerts.map(
                (alert, alertIndex) =>
                  alert.title && (
                    <li
                      key={alert.id}
                      className={styles.manageAlert__list__item}
                      data-cy="alert-management-list-item"
                    >
                      <AlertManageSnippet
                        alert={alert}
                        onError={handleErrorMessage}
                        alertIndex={alertIndex}
                      />
                    </li>
                  ),
              )}
            </ul>
          )}
          {userNewInsAlertsResponse?.alerts?.length > 0 && (
            <GenericPagination
              paginationMeta={userNewInsAlertsResponse.metaData}
              defaultLimit={defaultAlertLimit}
              handlePaginationChange={handlePaginationChange}
              handleItemsPerPageClick={handleItemsPerPageClick}
              itemsPerPage={itemsPerPage}
              dataCyPagination={{
                container: 'alerts-management-pagination-container',
                resultsLabel: 'alerts-management-results-label',
                resultsItem: 'alerts-management-results-item',
              }}
              showText={true}
            ></GenericPagination>
          )}
        </div>
      </main>
    </PageAuthenticationGuard>
  );
};

export default AlertManagement;
